import React, { useState, useEffect } from 'react';
import { X, User, Calendar, Mail, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Tables } from '../lib/types';

interface ViewBookingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelPackage: Tables<'travel_packages'>;
}

const ViewBookingsModal: React.FC<ViewBookingsModalProps> = ({ 
  isOpen, 
  onClose, 
  travelPackage 
}) => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadBookings();
    }
  }, [isOpen]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('travel_bookings')
        .select(`
          *,
          contact:contacts (
            id,
            name,
            email,
            phone,
            gender
          )
        `)
        .eq('travel_package_id', travelPackage.id)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;
      setBookings(data || []);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
      setError(err.message || 'Errore nel caricamento delle prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">Prenotazioni</h2>
            <p className="text-gray-600">{travelPackage.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Caricamento prenotazioni...</p>
          </div>
        ) : bookings.length > 0 ? (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div 
                key={booking.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-rose-50 rounded-full">
                    <User className="h-5 w-5 text-rose-500" />
                  </div>
                  <div>
                    <h3 className="font-medium">{booking.contact.name}</h3>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{booking.contact.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{booking.contact.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(booking.created_at).toLocaleDateString('it-IT')}</span>
                      </div>
                    </div>
                    {booking.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p className="font-medium">Note:</p>
                        <p>{booking.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Nessuna prenotazione</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewBookingsModal;